import React from 'react';
import { Sparkles, Leaf, Heart, Sun } from 'lucide-react';
import { useLanguage } from '../i18n';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { EditableText, EditableImage } from '../components/InlineEdit';

const iconMap = { Sparkles, Leaf, Heart, Sun };

const CulturePage = () => {
  const { t, language } = useLanguage();
  const { settings, getImageUrl } = useSiteSettings();

  // Parse values from settings
  let values = [];
  try {
    const valuesKey = `values_data_${language}`;
    const valuesJson = settings?.[valuesKey];
    if (valuesJson) {
      values = JSON.parse(valuesJson);
    }
  } catch (e) {
    values = language === 'pt' ? [
      { title: 'Excelência', desc: 'Buscamos a perfeição em cada detalhe.' },
      { title: 'Sustentabilidade', desc: 'Compromisso com práticas ecológicas.' },
      { title: 'Bem-Estar', desc: 'O seu conforto é a nossa prioridade.' },
      { title: 'Inovação', desc: 'Tecnologia de ponta aliada a técnicas tradicionais.' },
    ] : [
      { title: 'Excellence', desc: 'We seek perfection in every detail.' },
      { title: 'Sustainability', desc: 'Commitment to eco-friendly practices.' },
      { title: 'Well-being', desc: 'Your comfort is our priority.' },
      { title: 'Innovation', desc: 'Cutting-edge technology combined with traditional techniques.' },
    ];
  }

  const icons = [Sparkles, Leaf, Heart, Sun];
  const cultureImage = getImageUrl(settings?.culture_image) || 'https://res.cloudinary.com/dwpnw1fgc/image/upload/v1770159091/lourportf-8_giytc5.jpg';
  const yearsExp = settings?.years_experience || '10+';

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24" data-testid="culture-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <span className="text-sm uppercase tracking-widest text-[#BFA05F] font-medium">
              <EditableText settingKey="brand_name" defaultValue="Lou's Way" />
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#1A1815] mt-4">
              <EditableText 
                settingKey={`culture_title_${language}`} 
                defaultValue={t('culture.title')} 
              />
            </h1>
            <p className="text-lg text-[#635E54] mt-6 leading-relaxed">
              <EditableText 
                settingKey={`culture_subtitle_${language}`} 
                defaultValue={t('culture.subtitle')} 
                multiline 
              />
            </p>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((item, index) => {
              const IconComponent = icons[index % icons.length];
              return (
                <div
                  key={index}
                  className="bg-white p-10 rounded-3xl border border-[#E6E2DA] hover:border-[#BFA05F]/30 hover:shadow-xl transition-all duration-500 animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`culture-value-${index}`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-[#1A1815] flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-[#BFA05F]" />
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-[#1A1815] mb-4">
                    {item.title}
                  </h3>
                  <p className="text-[#635E54] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 md:py-24 bg-[#1A1815] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#BFA05F]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#BFA05F]/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="animate-fade-in-up">
              <span className="text-sm uppercase tracking-widest text-[#BFA05F] font-medium">
                {language === 'pt' ? 'Nossa Filosofia' : 'Our Philosophy'}
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-normal mt-4">
                {language === 'pt' ? 'Beleza que vem de ' : 'Beauty that comes from '}
                <span className="italic text-[#BFA05F]">{language === 'pt' ? 'dentro' : 'within'}</span>
              </h2>
              <p className="text-[#A8A29A] mt-6 leading-relaxed">
                <EditableText 
                  settingKey={`culture_philosophy_${language}`} 
                  defaultValue="Acreditamos que a verdadeira beleza transcende a aparência."
                  multiline
                  className="text-[#A8A29A]"
                />
              </p>
            </div>
            <div className="relative animate-fade-in-up animation-delay-200">
              <EditableImage
                settingKey="culture_image"
                defaultSrc="https://res.cloudinary.com/dwpnw1fgc/image/upload/v1770159091/lourportf-8_giytc5.jpg"
                alt="Lou's Way Philosophy"
                imgClassName="rounded-3xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#BFA05F] rounded-2xl p-6 shadow-xl">
                <p className="font-heading text-2xl text-[#1A1815]">
                  <EditableText settingKey="years_experience" defaultValue="10+" />
                </p>
                <p className="text-[#1A1815]/80 text-sm">
                  {language === 'pt' ? 'Anos de Experiência' : 'Years of Experience'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <div className="animate-fade-in-up">
            <Sparkles className="w-10 h-10 text-[#BFA05F] mx-auto mb-8" />
            <blockquote className="font-heading text-2xl md:text-3xl font-normal text-[#1A1815] italic leading-relaxed">
              "<EditableText settingKey="quote_text" defaultValue="A beleza começa no momento em que decides ser tu mesma." />"
            </blockquote>
            <p className="text-[#BFA05F] mt-6 text-sm uppercase tracking-widest">
              — <EditableText settingKey="quote_author" defaultValue="Coco Chanel" />
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CulturePage;
